import {
    CompareEntryPoint,
    CompareRowId,
    CompareSortOption,
    CompareType
} from '@/hotdesk-ui/enum/compare';
import { COMPARE_MAX_ITEMS_LIMIT, COMPARE_ROUTES } from './constants';
import type { CompareUrlState } from './types';

/**
 * The comparison table keeps its whole state in the link, so going back,
 * refreshing and sharing all open the same table.
 *
 *   /compare/workspaces?items=123,87,412&sort=price_low_high&hide=reviews,area
 *     &open=1,3&picks=456,902&from=workspace_details&back=/workspaces/...&backName=Hub%20North
 *
 * Only non-default values are written, so a fresh table has a short link.
 */

const PARAM = {
    ITEMS: 'items',
    SORT: 'sort',
    HIDE: 'hide',
    OPEN: 'open',
    PICKS: 'picks',
    FROM: 'from',
    BACK: 'back',
    BACK_NAME: 'backName'
} as const;

type SearchParamsInput =
    | URLSearchParams
    | Record<string, string | string[] | undefined>;

const SORT_VALUES = new Set<string>(Object.values(CompareSortOption));
const ROW_VALUES = new Set<string>(Object.values(CompareRowId));
const ENTRY_VALUES = new Set<string>(Object.values(CompareEntryPoint));

const readParam = (params: SearchParamsInput, key: string): string | null => {
    if (params instanceof URLSearchParams) return params.get(key);
    const value = params[key];
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
};

const splitList = (raw: string | null): string[] =>
    raw
        ? raw
              .split(',')
              .map((part) => part.trim())
              .filter(Boolean)
        : [];

/** Positive integer ids, de-duplicated, in their original order. */
const parseIds = (raw: string | null, limit?: number): number[] => {
    const ids: number[] = [];
    for (const part of splitList(raw)) {
        if (!/^\d+$/.test(part)) continue;
        const id = Number(part);
        if (id > 0 && !ids.includes(id)) ids.push(id);
        if (limit !== undefined && ids.length >= limit) break;
    }
    return ids;
};

/**
 * Only same-site paths are accepted, so a crafted link cannot turn the back
 * button into an open redirect.
 */
export const sanitizeBackPath = (raw: string | null): string | null => {
    if (!raw) return null;
    const path = raw.trim();
    if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
        return null;
    }
    return path;
};

export const parseCompareUrlState = (
    params: SearchParamsInput,
    maxItems: number = COMPARE_MAX_ITEMS_LIMIT
): CompareUrlState => {
    const limit = Math.min(Math.max(maxItems, 1), COMPARE_MAX_ITEMS_LIMIT);

    const sortRaw = readParam(params, PARAM.SORT);
    const fromRaw = readParam(params, PARAM.FROM);

    return {
        items: parseIds(readParam(params, PARAM.ITEMS), limit),
        sort:
            sortRaw && SORT_VALUES.has(sortRaw)
                ? (sortRaw as CompareSortOption)
                : null,
        hiddenRows: [
            ...new Set(
                splitList(readParam(params, PARAM.HIDE)).filter((row) =>
                    ROW_VALUES.has(row)
                )
            )
        ] as CompareRowId[],
        openTypes: parseIds(readParam(params, PARAM.OPEN)),
        listingPicks: parseIds(readParam(params, PARAM.PICKS), limit),
        from:
            fromRaw && ENTRY_VALUES.has(fromRaw)
                ? (fromRaw as CompareEntryPoint)
                : CompareEntryPoint.EXPLORE,
        back: sanitizeBackPath(readParam(params, PARAM.BACK)),
        backName: readParam(params, PARAM.BACK_NAME)?.trim() || null
    };
};

export const serializeCompareUrlState = (
    state: Partial<CompareUrlState>
): URLSearchParams => {
    const params = new URLSearchParams();

    if (state.items?.length) params.set(PARAM.ITEMS, state.items.join(','));
    if (state.sort) params.set(PARAM.SORT, state.sort);
    if (state.hiddenRows?.length) {
        params.set(PARAM.HIDE, state.hiddenRows.join(','));
    }
    if (state.openTypes?.length) {
        params.set(PARAM.OPEN, state.openTypes.join(','));
    }
    if (state.listingPicks?.length) {
        params.set(PARAM.PICKS, state.listingPicks.join(','));
    }
    if (state.from && state.from !== CompareEntryPoint.EXPLORE) {
        params.set(PARAM.FROM, state.from);
    }
    const back = sanitizeBackPath(state.back ?? null);
    if (back) params.set(PARAM.BACK, back);
    if (state.backName) params.set(PARAM.BACK_NAME, state.backName);

    return params;
};

/** Full link to a comparison table. */
export const buildCompareHref = (
    type: CompareType,
    state: Partial<CompareUrlState>
): string => {
    const query = serializeCompareUrlState(state).toString();
    return query ? `${COMPARE_ROUTES[type]}?${query}` : COMPARE_ROUTES[type];
};
